import { html, css } from 'lit';
import { ariaLabelWithNewTab } from '../../common/utils/a11y-util';
import { customElement, state } from 'lit/decorators.js';
import { connect } from 'pwa-helpers';
import store from '../../store/store';
import { BaseComponent } from '../../features/base-component/base-component';
import { StoreState } from '../../store/store.interface';
import { getTunnelExitLink } from '../cart/shop-cart/store/shop-cart.selectors';
import emit from '../../common/events';
import { Dropdown } from 'bootstrap';

const HELP_URL = "https://mh.my.site.com/CXG/s/";

@customElement('mhecomm-tunnel-header-links')
export class TunnelHeaderLinks extends connect(store)(BaseComponent) {

  static styles = css`
    .dropdown-menu {
      z-index: 1000 !important;
    }

    .nav-link {
      display: flex;
      align-items: center;
      gap: 0.5rem;
    }

    .dropdown-btn {
      padding: 0.5rem !important;
      display: flex;
      align-items: center;
      justify-content: center;
      min-width: 44px;
      min-height: 44px;
      border-radius: 0.375rem;
      transition: background-color 0.15s ease-in-out, box-shadow 0.15s ease-in-out;
      cursor: pointer;
    }

    .dropdown-btn:hover {
      background-color: rgba(0, 0, 0, 0.1);
    }

    .dropdown-btn:focus {
      background-color: rgba(0, 0, 0, 0.1);
      box-shadow: 0 0 0 0.25rem rgba(13, 110, 253, 0.25);
      outline: none;
    }

    .dropdown-btn:active {
      background-color: rgba(0, 0, 0, 0.2);
      transform: translateY(1px);
    }
  `;

  @state()
  private exitUrl;

  private bsDropdown?: Dropdown;
  private dropdownElement?: HTMLElement;

  stateChanged(state: StoreState) {
    this.exitUrl = getTunnelExitLink(state);
  }

  protected updated(_changedProperties: Map<string | number | symbol, unknown>): void {
    this.bsDropdown = this.bsInit('[data-bs-toggle="dropdown"]', window.bootstrap?.Dropdown)?.instance;
    this.dropdownElement = this.getDropdownMenu();
    if (this.bsDropdown) {
      this.bsDropdown?.hide();
    }
  }

  renderComp() {
    return html`
      <!-- Regular navigation for md and larger screens -->
      <nav class="d-none d-md-flex">
        ${this.renderHelpLink('nav-link')}
        ${this.renderSignOutLink('nav-link')}
      </nav>

      <!-- Dropdown navigation for small screens -->
      <div class="d-block d-md-none">
        <div class="dropdown">
          <button
            id="tunnel-header-dropdown-btn"
            data-automation-id="tunnel-header-dropdown-btn"
            type="button"
            class="btn dropdown-btn"
            aria-expanded=${this.dropdownElement ? this.isDropdownExpanded(this.dropdownElement) : false}
            aria-label="Navigation menu"
            data-bs-toggle="dropdown"
            data-bs-auto-close="true"
            title="Menu"
            @click=${(event: any) => this._handleDropdownToggle(event)}
          >
            ${this.uxtIconReady ? html`
              <uxt-icon icon="three-dots" aria-hidden="true" size="2xl"></uxt-icon>
            ` : ''}
          </button>
          
          <ul
            id="tunnel-header-dropdown-menu"
            aria-labelledby="tunnel-header-dropdown-btn"
            class="dropdown-menu dropdown-menu-end"
          >
            <li>${this.renderHelpLink('dropdown-item')}</li>
            <li>${this.renderSignOutLink('dropdown-item')}</li>
          </ul>
        </div>
      </div>
    `;
  }

  private renderHelpLink(linkClass: string) {
    const isDropdownItem = linkClass === 'dropdown-item';
    const automationId = isDropdownItem ? 'tunnel-help-link-dropdown' : 'tunnel-help-link';
    const combinedClass = isDropdownItem 
      ? `${linkClass} d-flex align-items-center`
      : `${linkClass} link-dark text-decoration-none`;

    return html`
      <a
        data-automation-id="${automationId}"
        class="${combinedClass}"
        @click=${this.analyticsHelpLink}
        href=${HELP_URL}
        aria-label="${ariaLabelWithNewTab('Help')}"
        target="_blank"
        rel="noopener noreferrer"
      >
        ${this.uxtIconReady ? 
          isDropdownItem ? html`
            <uxt-icon icon="question-circle" aria-hidden="true" class="me-2"></uxt-icon>
          ` : html`
            <uxt-icon id="help-icon" icon="question-circle" aria-hidden="true" size="xl"></uxt-icon>
          `
          : ''}
        <span>Help</span>
      </a>
    `;
  }

  private renderSignOutLink(linkClass: string) {
    const isDropdownItem = linkClass === 'dropdown-item';
    const automationId = isDropdownItem ? 'tunnel-exit-link-dropdown' : 'tunnel-exit-link';
    const combinedClass = isDropdownItem 
      ? `${linkClass} d-flex align-items-center`
      : `${linkClass} link-dark text-decoration-none`;

    return html`
      <a
        data-automation-id="${automationId}"
        @click=${this.analyticsExitLink}
        class="${combinedClass}"
        href="${this.exitUrl || '#'}"
        aria-label="Sign out of current session"
      >
        ${this.uxtIconReady ? 
          isDropdownItem ? html`
            <uxt-icon icon="box-arrow-right" aria-hidden="true" class="me-2"></uxt-icon>
          ` : html`
            <uxt-icon id="signout-icon" icon="box-arrow-right" aria-hidden="true" size="xl"></uxt-icon>
          `
          : ''}
        <span>Sign Out</span>
      </a>
    `;
  }

  analyticsHelpLink() {
    emit({
      element: this,
      name: 'tunnel-payment-help',
      detail: {
        url: HELP_URL,
        id: 'tunnel-payment-help'
      }
    })
  }

  analyticsExitLink() {
    emit({
      element: this,
      name: 'tunnel-payment-exit',
      detail: {
        url: this.exitUrl,
        id: 'tunnel-payment-exit'
      }
    })
    sessionStorage.clear();
  }

  private _handleDropdownToggle(event: any): void {
    const SPACE_KEY_CODE = 32;
    if (event && (event.type === 'click' || (event.type === 'keyup' && event.which === SPACE_KEY_CODE))) {
      this.bsDropdown?.toggle();
      
      this.addEventListener('focusout', (): void => {
        this.bsDropdown?.hide();
      });
    }
    emit({
      element: this,
      name: 'click',
      detail: {
        eventName: 'tunnel-header-dropdown-btn',
        id: 'tunnel-header-dropdown-btn'
      }
    });
  }

  private getDropdownMenu(): HTMLElement {
    return this.shadowRoot!.querySelector(`#tunnel-header-dropdown-menu`) as HTMLElement;
  }

  private isDropdownExpanded(dropdown: HTMLElement): boolean {
    return dropdown?.classList?.contains('show') ? true : false;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'mhecomm-tunnel-header-links': TunnelHeaderLinks;
  }
}
